import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome } from "@fortawesome/pro-regular-svg-icons";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { getAgreementByPublicId } from "../api/Agreement";
import { getUser } from "../api/Users";
import { getTenant } from "../api/Tenants";
import { getLicense } from "../api/Licenses";
import { getAddOnService } from "../api/AddOnService";
import { useAuth } from "../providers/AuthContext";
import { useSidebar } from "../providers/SidebarContext";
import { getBillingGroup } from "../api/BillingGroups";
import { getAccountType } from "../api/Agreement";
import { getIndexAdjustment } from "../api/BillingGroups";
import { getUserDeviceByPublicId } from "../api/Agreement";
import { getHardwareProduct } from "../api/HardwareProducts";

const Breadcrumbs = () => {
  const location = useLocation();
  const { 
    agreementPublicId, 
    tenantPublicId, 
    userPublicId, 
    licensePublicId, 
    addOnServicePublicId,
    billingGroupPublicId,
    accountTypePublicId,
    indexAdjustmentPublicId,
    userDevicePublicId,
    hardwareProductPublicId,
     } = useParams();
  const { getAccessToken } = useAuth();
  const [agreementName, setAgreementName] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [userName, setUserName] = useState("");
  const [licenseName, setLicenseName] = useState("");
  const [addOnServiceName, setAddOnServiceName] = useState("");
  const [billingGroupName, setBillingGroupName] = useState("");
  const [accountTypeName, setAccountTypeName] = useState("");
  const [indexAdjustmentName, setIndexAdjustmentName] = useState("");
  const [userDeviceName, setUserDeviceName] = useState("");
  const [hardwareProductName, setHardwareProductName] = useState("");

  useEffect(() => {
    const fetchNames = async () => {
      const accessToken = await getAccessToken();
      
      try {
        if (agreementPublicId) {
          const agreement = await getAgreementByPublicId(agreementPublicId, accessToken);
          setAgreementName(agreement.name || agreement.agreementName || "Agreement"); 
        }
        if (tenantPublicId) {
          const tenant = await getTenant(tenantPublicId, accessToken);
          setTenantName(tenant.displayName || tenant.name || "Tenant");
        }
        if (userPublicId) {
          const user = await getUser(userPublicId, accessToken);
          setUserName(user.displayName || user.userPrincipalName || "User"); 
        }
        if (licensePublicId) {
          const license = await getLicense(licensePublicId, accessToken);
          setLicenseName(license.name || "License");
        }
        if (addOnServicePublicId) {
          const addOnService = await getAddOnService(addOnServicePublicId, accessToken);
          setAddOnServiceName(addOnService.name || "Add-on service");          
        }
        if (billingGroupPublicId) {
          const billingGroup = await getBillingGroup(billingGroupPublicId, accessToken);
          setBillingGroupName(billingGroup.name || "Billing group");          
        }
        if (accountTypePublicId) {
          const accountType = await getAccountType(accountTypePublicId, accessToken);
          setAccountTypeName(accountType.name || "Account type");          
        }
        if (indexAdjustmentPublicId) {
          const indexAdjustment = await getIndexAdjustment(indexAdjustmentPublicId, accessToken);
          setIndexAdjustmentName(indexAdjustment.name || "Index adjustment");          
        }
        if (userDevicePublicId) {
          const userDevice = await getUserDeviceByPublicId(userDevicePublicId, accessToken);
          setUserDeviceName(userDevice.displayName || "User device");
        }
        if (hardwareProductPublicId) {
          const hardwareProduct = await getHardwareProduct(hardwareProductPublicId, accessToken);
          setHardwareProductName(hardwareProduct.name || "Hardware product");
        }
      } catch (error) {
        console.error("Failed to fetch names for breadcrumbs:", error);
      }
    };

    fetchNames();
  }, [agreementPublicId, 
    tenantPublicId, 
    userPublicId, 
    licensePublicId, 
    addOnServicePublicId, 
    getAccessToken,
    billingGroupPublicId,
    accountTypePublicId,
    indexAdjustmentPublicId,
    userDevicePublicId,
    hardwareProductPublicId,
  ]);

  let pathnames = location.pathname.split("/").filter((x) => x);

  // Remove the last segment if it is "Edit"
  if (pathnames[pathnames.length - 1] === "edit") {
    pathnames = pathnames.slice(0, -1);
  }

  const getBreadCrumbName = (segment) => {
    if (segment === agreementPublicId) return agreementName || segment;
    if (segment === tenantPublicId) return tenantName || segment;
    if (segment === userPublicId) return userName || segment;
    if (segment === licensePublicId) return licenseName || segment;
    if (segment === addOnServicePublicId) return addOnServiceName || segment;
    if (segment === billingGroupPublicId) return billingGroupName || segment;
    if (segment === accountTypePublicId) return accountTypeName || segment;
    if (segment === indexAdjustmentPublicId) return indexAdjustmentName || segment;
    if (segment === userDevicePublicId) return userDeviceName || segment;
    if (segment === hardwareProductPublicId) return hardwareProductName || segment;
    if (segment === "hardware-catalogue") return "Hardware catalogue";
    return capitalize(segment);
  };

  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <nav className="breadcrumb">
      <ul className="flex items-center">
        <li className="text-primary-600 text-xs">
          <Link to="/">
          <FontAwesomeIcon icon={faHome} className="mr-1" />
          Home
          </Link>
        </li>
        {pathnames.length > 0 && <li className="mx-1">/</li>}

        {pathnames.map((value, index) => {
          const to = `/${pathnames.slice(0, index + 1).join("/")}`;
          const isLast = index === pathnames.length - 1;
          return (
            <React.Fragment key={to}>
              <li className="text-xs">
                {isLast ? (
                  <span>{getBreadCrumbName(value)}</span>
                ) : (
                  <Link to={to} className="text-xs">{getBreadCrumbName(value)}</Link>
                )}
              </li>
              {!isLast && <li className="mx-1">/</li>}
            </React.Fragment>
          );
        })}
      </ul>
    </nav>
  );
};

export default Breadcrumbs;
